<template>
  <div class="TaskPanel">
    <b-overlay :show="saving" rounded="sm">
    <section class="task-panel-section">
      <div class="details-header">
        <h4 class="details-title">Add New Task</h4>
        <div class="details-header-actions">
          <button type="button" class="btn btn-success btn-sm mr-2" @click="save" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-2"></b-spinner>
            <i v-else class="fa fa-plus mr-2"></i>
            {{ saving ? 'Adding...' : 'Add Task' }}
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="closePanel" :disabled="saving">Cancel</button>
        </div>
      </div>

      <div class="card panel task-details-main">
        <div class="task-details-main-item">
          <label class="detail-label-block">Task Name <span class="required">*</span></label>
          <input
            type="text"
            class="task-name-input"
            v-model="form.name"
            placeholder="Enter task name"
            ref="nameInput"
          />
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Description</label>
          <!-- <at> works here because slide-out panel isn't position:fixed in a way that breaks
               getOffset() (verified by TodoDetails.vue using the same pattern). -->
          <at at="@" :members="activeEmployees" name-key="Name" v-if="employees" ref="empat">
            <template slot="item" slot-scope="s">
              <span tabindex="-1">{{ s.item.Name }}</span>
            </template>
            <template slot="embeddedItem" slot-scope="s">
              <span>
                <span tabindex="-1" :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{ s.current.Name + " " }}</span>
              </span>
            </template>
            <div class="details-editor"
              ref="detailsEditor"
              placeholder="Add a description... Use @ to mention a teammate."
              contenteditable
              @input="onDetailsInput"></div>
          </at>
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Type</label>
          <input
            type="text"
            class="task-input"
            v-model="form.type"
            placeholder="e.g., Feature, Bug, Task"
          />
        </div>

        <div class="task-details-main-item">
          <label class="detail-label-block">Tags</label>
          <multiselect
            v-model="form.tags"
            :options="[]"
            track-by="name"
            :multiple="true"
            :taggable="true"
            tag-placeholder="Press Enter to add as a new tag"
            placeholder="Type to add tags..."
            @tag="onAddTag"
            label="name"
            :close-on-select="true"
            :clear-on-select="false"
            :preserve-search="false"
            :preselect-first="false"
            :hide-selected="true"
          />
        </div>
      </div>
    </section>
    </b-overlay>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import Multiselect from 'vue-multiselect';
import At from '@/pages/TaskList/vueat/At';
import { getUniqueLinkedEmployeeIDs } from '@/utils/taskMentions';
import { buildTagAlertPayload } from '@/utils/kanbanTagAlerts';
import { isEmailEnabled } from '@/utils/siteCapability';

export default {
  name: 'AddTaskPanel',
  components: { Multiselect, At },
  props: {
    boardId: { type: String, required: true },
    columnId: { type: String, required: true },
  },
  data() {
    return {
      form: {
        name: '',
        tags: [],
        type: '',
        date: moment().toDate(),
      },
      detailsHTML: '',
      saving: false,
    };
  },
  computed: {
    ...mapState('customer', {
      userID: state => state.userID,
      siteID: state => state.siteID,
      user: state => state.user,
      employees: state => state.employees,
      settings: state => state.settings,
    }),
    // Resolve the parent board from the store so we can read its
    // accessibleBy without prop-drilling. Returns null if the board has
    // been evicted (e.g. user removed mid-session).
    parentBoard() {
      const boards = this.$store && this.$store.state && this.$store.state.kanban && this.$store.state.kanban.boards;
      if (!Array.isArray(boards) || !this.boardId) return null;
      return boards.find(b => b.id === this.boardId) || null;
    },
    activeEmployees() {
      if (!this.employees) return [];
      try {
        const all = this.employees.filter(e => e.Active === 1 || e.Active === '1');
        // On a private board, restrict the @-mention picker to board
        // members. Mentioning a non-member would create a notification
        // the mentioned person can't follow (the board is hidden from
        // them by Firestore rules).
        const acc = this.parentBoard && this.parentBoard.accessibleBy;
        if (Array.isArray(acc) && acc.length > 0 && !(acc.length === 1 && acc[0] === '*')) {
          return all.filter(e => acc.includes(e.EmployeeID));
        }
        return all;
      } catch (_) {
        return [];
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.nameInput) this.$refs.nameInput.focus();
      if (this.$refs.detailsEditor) this.$refs.detailsEditor.innerHTML = '';
    });
  },
  methods: {
    onDetailsInput() {
      if (this.$refs.detailsEditor) {
        this.detailsHTML = this.$refs.detailsEditor.innerHTML || '';
      }
    },

    onAddTag(newTag) {
      if (typeof newTag === 'string' && newTag.includes('@')) {
        this.$toast.warning('Mentions go in the Description field, not Tags. Use @ in "Description" so they get notified.');
        return;
      }
      const existsIndex = this.form.tags.findIndex(t => t.name === newTag);
      if (existsIndex === -1) this.form.tags.push({ name: newTag });
    },

    ...mapActions('kanban', ['addTask', 'sendTagAlert']),

    // Fire a per-board tag alert if the board has tag-alerts enabled and a
    // newly-added tag matches. Fire-and-forget: sendTagAlert swallows all
    // errors, so this NEVER blocks the card save / panel close. Called only
    // AFTER the Firestore write succeeds (an alert for a card that failed to
    // save would be a lie).
    maybeFireTagAlert(oldTags, newTags, cardName) {
      try {
        // EMAIL-CAPABILITY GATE: don't POST for a site that can't send email
        // (backend enforces the same; this is the cheap client-side guard).
        // Uses the canonical helper (src/utils/siteCapability.js).
        if (!isEmailEnabled(this.settings)) return;
        // The board-config + newly-added-tag decision + payload shape is shared
        // with EditTaskPanel — see buildTagAlertPayload (kanbanTagAlerts.js).
        const payload = buildTagAlertPayload({
          board: this.parentBoard,
          siteID: this.siteID,
          oldTags,
          newTags,
          cardName,
          addedByEmployeeID: this.userID,
          addedByName: this.user && this.user.Name,
        });
        if (payload) this.sendTagAlert(payload);
      } catch (_) { /* never block the save on alert dispatch */ }
    },

    async save() {
      if (!this.form.name) {
        this.$toast.error('Missing Task Name. Please try again.');
        return;
      }
      this.saving = true;
      try {
        const detailsHTML = this.$refs.detailsEditor
          ? (this.$refs.detailsEditor.innerHTML || '')
          : this.detailsHTML;

        const mentionedIds = getUniqueLinkedEmployeeIDs(detailsHTML);
        const notification = [];
        const creatorName = (this.user && this.user.Name) ? this.user.Name : 'a teammate';
        for (const assignee of mentionedIds) {
          if (assignee === this.userID) continue;
          notification.push({
            audience: 'EMPLOYEE',
            employeeID: assignee,
            type: 'KANBAN',
            text: `You have been mentioned on a Kanban card by ${creatorName}`,
            siteID: this.siteID,
            createdAt: new Date(),
            unreadList: [assignee],
          });
        }

        const task = {
          id: Date.now().toString(),
          name: this.form.name,
          tags: this.form.tags,
          type: this.form.type,
          date: this.form.date,
          details: detailsHTML,
        };

        // Own the save INSIDE the panel so the parent's handle.promise.then
        // doesn't race with the plugin's close transition. Mirrors TodoDetails.vue
        // pattern (await the write, then emit closePanel).
        await this.addTask({
          boardId: this.boardId,
          columnId: this.columnId,
          task,
          ...(notification.length > 0 && { notification }),
        });

        // New card: diff [] -> the card's tags. Fire AFTER the write resolves.
        this.maybeFireTagAlert([], this.form.tags, this.form.name);

        this.$toast.success('Task added!');
        this.$emit('closePanel', { action: 'saved', task });
      } catch (e) {
        console.error('[AddTaskPanel] save failed:', e);
        this.$toast.error('Error adding task. Please try again.');
        // Keep the panel open so the user can retry or cancel manually.
      } finally {
        this.saving = false;
      }
    },

    closePanel() {
      this.$emit('closePanel', { action: 'cancel' });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Styles mirror TodoDetails.vue so the Kanban panel feels like the Task panel. */

.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.375rem;
}

.required {
  color: #ef4444;
  font-weight: 700;
}

.task-name-input,
.task-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #fff;
  color: #0f172a;
  font-size: 0.9375rem;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: #94a3b8;
  }
}

.details-editor {
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  background-color: white;
  color: #0f172a;
  transition: border-color 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &:empty::before {
    content: attr(placeholder);
    color: #94a3b8;
    pointer-events: none;
  }
}

</style>
